import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { api } from '@/api/client';
import { queryKeys } from '@liniya/client-core';
import { ErrorBoundary } from '@/app/ErrorBoundary';
import { MobileNav, Rail } from '@/components/Rail';
import { ChatInfo } from '@/features/chats/ChatInfo';
import { ChatListPanel } from '@/features/chats/ChatListPanel';
import { sortChats, useChat, useChats } from '@liniya/client-core';
import { ConversationView, EmptyConversation } from '@/features/messaging/ConversationView';
import { useCall } from '@/features/calls/CallProvider';
import { useSession, useViewer } from '@/features/auth/session';
import { useProfile } from '@liniya/client-core';
import { useUiStore } from '@liniya/client-core';

/**
 * The messenger screen.
 *
 * The URL owns which conversation is open, so a chat can be linked to,
 * bookmarked, reloaded and reached with the browser's back button. The store
 * mirrors it only for the phone layout, where "which chat" and "is the
 * conversation covering the list" are two different questions.
 */
export function MessengerPage() {
  const { chatId } = useParams<{ chatId?: string }>();
  const navigate = useNavigate();
  const viewer = useViewer();
  const { deviceId } = useSession();
  const profile = useProfile();
  const chats = useChats();
  const activeChat = useChat(chatId ?? null);
  const call = useCall();

  const chatOpen = useUiStore((state) => state.chatOpen);
  const detailsOpen = useUiStore((state) => state.detailsOpen);
  const openChatInStore = useUiStore((state) => state.openChat);
  const closeChat = useUiStore((state) => state.closeChat);

  const config = useQuery({
    queryKey: queryKeys.config,
    queryFn: () => api.config.get(),
    staleTime: 30 * 60_000,
  });

  useEffect(() => {
    if (chatId) openChatInStore(chatId);
    else closeChat();
  }, [chatId, closeChat, openChatInStore]);

  const ordered = useMemo(() => sortChats(chats.data ?? []), [chats.data]);
  const unreadTotal = useMemo(
    () => ordered.reduce((total, chat) => total + (chat.membership.is_muted ? 0 : chat.unread_count), 0),
    [ordered],
  );

  // The tab title is the only unread indicator visible when the window is in
  // the background, which is where most notifications are actually noticed.
  useEffect(() => {
    document.title = unreadTotal > 0 ? `(${unreadTotal}) Линия` : 'Линия';
  }, [unreadTotal]);

  const openChat = useCallback(
    (nextChatId: string) => {
      navigate(`/chats/${nextChatId}`);
    },
    [navigate],
  );

  const back = useCallback(() => {
    navigate('/chats');
  }, [navigate]);

  const gridClassName = [
    'messenger-grid',
    chatOpen ? 'chat-open' : '',
    detailsOpen ? 'details-open' : 'details-hidden',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <main className={gridClassName}>
      <Rail
        displayName={profile.data?.effectiveDisplayName ?? viewer.email}
        avatarUrl={profile.data?.avatarUrl ?? null}
        unreadTotal={unreadTotal}
      />

      <ErrorBoundary area="chat-list">
        <ChatListPanel
          chats={ordered}
          activeChatId={chatId ?? null}
          loading={chats.isPending}
          error={chats.isError}
          onOpenChat={openChat}
          onRetry={() => void chats.refetch()}
        />
      </ErrorBoundary>

      {activeChat.data && deviceId ? (
        <ConversationView
          chat={activeChat.data}
          viewerUserId={viewer.id}
          deviceId={deviceId}
          limits={config.data?.limits}
          onBack={back}
          onStartCall={(kind) => {
            if (activeChat.data?.peer_user_id) {
              void call.start({
                chatId: activeChat.data.id,
                calleeUserId: activeChat.data.peer_user_id,
                mediaKind: kind,
                peerName: activeChat.data.title,
              });
            }
          }}
          onOpenDetails={() => useUiStore.getState().openDetails()}
        />
      ) : (
        <EmptyConversation />
      )}

      {activeChat.data ? (
        <ErrorBoundary area="chat-info">
          <ChatInfo chat={activeChat.data} onLeft={back} />
        </ErrorBoundary>
      ) : (
        <aside className="details" />
      )}

      <MobileNav unreadTotal={unreadTotal} />
    </main>
  );
}
